import { useState } from 'react';
import { X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { formatRecommendationCurrency } from '@/api/recommendationFormat';
import type { CampaignActionRow } from '@/types/campaignRecommendations';

export function PauseConfirmModal({
  profileId,
  row,
  isOpen,
  onClose,
  onSuccess,
  onToast,
}: {
  profileId: string;
  row: CampaignActionRow;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onToast: (message: string, variant?: 'success' | 'error') => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adsApi.updateSpCampaignState(profileId, row.campaignId, {
        state: 'PAUSED',
      });
      if (!res.success) throw new Error('Failed to pause campaign');
      onToast(`${row.campaignName} paused.`, 'success');
      onSuccess();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to pause campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const savings = row.projectedSavings ?? row.cost ?? 0;
  const lossAvoided = row.projectedLossAvoided ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Pause campaign</h3>
          <button type="button" onClick={onClose} className="text-chrome-label hover:text-foreground-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-2 text-sm text-foreground-muted">
          Pause <span className="font-medium">{row.campaignName}</span>?
        </p>
        <div className="mb-3 rounded-xl border border-warning/20 bg-warning-subtle px-3 py-2 text-sm text-warning-ink">
          Over a similar period you could avoid about <strong>{formatRecommendationCurrency(savings)}</strong>{' '}
          in ad spend
          {lossAvoided > 0 ? (
            <>
              {' '}
              and about <strong>{formatRecommendationCurrency(lossAvoided)}</strong> of unprofitable loss
            </>
          ) : null}
          .
        </div>
        {row.rationale ? <p className="mb-4 text-sm text-foreground-subtle">{row.rationale}</p> : null}
        {error ? <p className="mb-3 text-sm text-error">{error}</p> : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-border py-2 text-sm"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="flex-1 rounded-lg bg-warning py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {submitting ? 'Pausing…' : 'Pause campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}
